import { appLogger } from '@/utils/logger';

export type ReactBoundaryErrorPayload = {
  name: string;
  message: string;
  componentStack?: string;
  release: string;
};

declare global {
  interface Window {
    /** Optional host-injected hook (e.g. Sentry, Datadog RUM). Must not throw. */
    __chargehubReportError?: (payload: ReactBoundaryErrorPayload) => void;
  }
}

/**
 * R4: central place for React error-boundary reporting. Logs safely (no PII); optional `window.__chargehubReportError` for APM.
 */
export function reportReactBoundaryError(
  error: Error,
  info: { componentStack?: string }
): void {
  const release = process.env.NEXT_PUBLIC_APP_VERSION?.trim() || 'unknown';
  appLogger.error('AppErrorBoundary: render failure', {
    name: error.name,
    message: error.message,
    componentStack: info.componentStack?.slice(0, 800),
    release,
  });

  if (typeof window === 'undefined') return;
  const hook = window.__chargehubReportError;
  if (typeof hook !== 'function') return;
  try {
    hook({
      name: error.name,
      message: error.message.slice(0, 2000),
      componentStack: info.componentStack?.slice(0, 2000),
      release,
    });
  } catch {
    /* host hook must not break the app */
  }
}
